import React from 'react'
import PropTypes from 'prop-types'
import TiledListItemShape from './prop_shapes/tiledListItem'
import { titleCase } from 'title-case'
import DOMPurify from 'dompurify'
import { POST_ICON_MAPPING, BG_IMAGE_MAPPING } from './Constants'
import TrackedLink from './TrackedLink'
import TiledListLineItem from './TiledListLineItem'

const TiledListItems = ({ listItems, supplierId = null, linkTracking = true }) => {
  const renderIcon = (theme, category) => {
    return <img className='padding-right-05' src={POST_ICON_MAPPING[theme][category]} />
  }

  const renderTitleRow = (title, theme, category) => {
    return (
      <div className='title-row'>
        {renderIcon(theme, category)}
        <span className='category pro-text-xs'>
          {titleCase(category.replace(/_/g, ' '))}
        </span>
        {title && renderTitle(title)}
      </div>
    )
  }

  const renderTitle = (title) => {
    return (
      <div className='title pro-text-s pro-bold'>
        {title}
      </div>
    )
  }

  const renderSummary = (summary, category) => {
    return (
      <>
        {summary &&
          <div className={`summary ${category === 'customer_testimonial' ? 'pro-italic pro-text-s' : 'pro-text-xs'}`}>
            {summary}
          </div>}
      </>
    )
  }

  const renderOverlay = (title, theme, category, summary) => {
    return (
      <>
        {renderTitleRow(title, theme, category)}
        {renderSummary(summary, category)}
      </>
    )
  }

  const renderVideoOverlay = (title, summary, category) => {
    return (
      <div className='grid-y align-middle align-center video-overlay stretch' data-test-video-overlay>
        <div className='cell shrink'>
          {renderTitle(title)}
        </div>
        <div className='cell shrink'>
          {renderSummary(summary, category)}
        </div>
        <div className='cell shrink play-icon'>
          <i className='fas fa-play' />
        </div>
      </div>
    )
  }

  const renderListItem = ({ id, type, imageUrl, bgImage, category, theme, url, title, summary, fullWidth }) => {
    const tiledLineItemKey = id || type
    const backgroundImage = imageUrl || BG_IMAGE_MAPPING[bgImage]
    const imageStyle = backgroundImage
      ? {
          background: `linear-gradient(0deg, rgba(42, 53, 63, 0.29), rgba(42, 53, 63, 0.29)), linear-gradient(180deg, rgba(42, 53, 63, 0.9) 0%, rgba(42, 53, 63, 0.9) 0.01%, rgba(42, 53, 63, 0.549) 100%), url('${DOMPurify.sanitize(backgroundImage)}')`,
          backgroundSize: 'cover'
        }
      : {}

    const listItemContent = (
      <div
        className={`recent-post ${category.replace(/_/g, '-')} ${theme} ${url ? 'clickable' : ''}`}
        style={imageStyle}
      >
        {category === 'video' ? renderVideoOverlay(title, summary, category) : renderOverlay(title, theme, category, summary)}
      </div>
    )

    let listItemChildren
    if (url && linkTracking) {
      listItemChildren = (
        <TrackedLink
          url={url}
          target='_blank'
          aria-label={title}
          eventType='supplier_post_link'
          supplierId={supplierId}
        >
          {listItemContent}
        </TrackedLink>
      )
    } else if (url) {
      listItemChildren = (
        <a href={url} aria-label={title} data-test-link-type='untracked' target='_blank' rel='noreferrer'>
          {listItemContent}
        </a>
      )
    } else {
      listItemChildren = listItemContent
    }

    return (
      <TiledListLineItem
        fullWidth={fullWidth}
        key={tiledLineItemKey}
      >
        {listItemChildren}
      </TiledListLineItem>
    )
  }

  return (
    <ul className='grid-x grid-padding-x grid-padding-y recent-posts' data-testid='recent-posts-list'>
      {listItems.map(renderListItem)}
    </ul>
  )
}

TiledListItems.propTypes = {
  listItems: PropTypes.arrayOf(TiledListItemShape),
  linkTracking: PropTypes.bool
}

TiledListItems.defaultProps = {
  listItems: [],
  linkTracking: true
}

export default TiledListItems
